<script lang="ts" module>
  import type { ISbResult } from '@storyblok/svelte';
  import type { Page } from '@agapi/mlc-core';

  export interface HomePageProps {
    data: {
      frontPageData: Page;
    };
  }
</script>

<script lang="ts">
  import { page } from '$app/state';
  import { browser } from '$app/environment';
  import { PUBLIC_BRAND_SLUG, PUBLIC_BRAND_NAME } from '$env/static/public';
  import { untrack } from 'svelte';
  import { StoryblokComponent } from '@storyblok/svelte';

  import { JsonLd } from '@agapi/ui/json-ld';
  import { AlternateLinks } from '@agapi/ui/alternate-links';
  import { getAlternateLinks } from '@glimja/shared/utils';

  const { data }: HomePageProps = $props();

  let story = $state<ISbResult['data'] | null>(null);

  const isPreview = browser && new URLSearchParams(window.location.search).has('_storyblok');

  $effect(() => {
    untrack(async () => {
      if (isPreview) {
        console.log('isPreview')

        const { getStoryblokApi, useStoryblokBridge } = await import('@storyblok/svelte');
        const storyblokApi = getStoryblokApi();

        story = (await storyblokApi.get(`cdn/stories/${PUBLIC_BRAND_SLUG}/front-page`, {
          version: "published",
          resolve_relations: ['story_row.collections', 'collection.banner', 'collection_card_grid.collections'],
        })).data.story;

        useStoryblokBridge(story.id, (newStory) => (story = newStory), {preventClicks: true});
      }
    });
  });

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: PUBLIC_BRAND_NAME,
    url: `${page.url.origin}`,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${page.url.origin}/sok?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };
</script>

<svelte:head>
  <title>{data.frontPageData?.meta?.title ?? data.frontPageData?.name}</title>
  <meta name="description" content={data.frontPageData?.meta?.description ?? ''} />
  <link rel="canonical" href={`${page.url.origin}`} />
  <meta property="og:title" content={data.frontPageData?.meta?.title ?? data.frontPageData?.name} />
  <meta property="og:description" content={data.frontPageData?.meta?.description ?? ''} />
  <meta property="og:url" content={`${page.url.origin}`} />
  <meta property="og:type" content="website" />
  <meta property="og:image" content={data.frontPageData?.meta?.image ?? ''} />
</svelte:head>

<AlternateLinks links={getAlternateLinks(page.url)} />

<JsonLd data={schemaData} />

{#if story}
  <StoryblokComponent blok={story.content} />
{:else}
  {#each data.frontPageData.content as blok}
    <StoryblokComponent blok={blok} />
  {/each}
{/if}
